import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Container from './Container'
import { Link as RouterLink } from 'react-router-dom'
import MuiButton from '@mui/material/Button'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

interface HeroSectionProps {
  headline: string
  subtext?: string
  primaryCta?: { label: string; to: string }
  secondaryCta?: { label: string; to: string }
  image?: { src: string; alt: string }
  /** Use Night Forest panel instead of the default dark gradient */
  lightBg?: boolean
  /** Display the hero image as a square instead of a circle */
  imageSquare?: boolean
}

export default function HeroSection({ headline, subtext, primaryCta, secondaryCta, image, lightBg, imageSquare }: HeroSectionProps) {

  // Night Forest two-panel hero: text left, image flush right
  if (lightBg && imageSquare && image) {
    return (
      <Box sx={{ backgroundColor: '#0B2027', overflow: 'hidden', position: 'relative' }}>
        <Container sx={{ py: { xs: 6, md: 9 }, position: 'relative', zIndex: 1 }}>
          <Box sx={{ maxWidth: { xs: '100%', md: '52%' } }}>
            <Typography
              variant="h1"
              sx={{
                color: '#F0F4F4',
                fontSize: { xs: '2.1rem', sm: '2.5rem', md: '3.4rem' },
                lineHeight: 1.1,
                letterSpacing: '-0.025em',
                fontWeight: 800,
              }}
            >
              {headline}
            </Typography>
            {subtext && (
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(240, 244, 244, 0.8)',
                  fontSize: { xs: '1rem', md: '1.12rem' },
                  lineHeight: 1.65,
                  mt: 2,
                }}
              >
                {subtext}
              </Typography>
            )}
            {(primaryCta || secondaryCta) && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1.5, sm: 2 }}
                sx={{ mt: { xs: 3.5, md: 4.5 } }}
              >
                {primaryCta && (
                  <MuiButton
                    component={RouterLink}
                    to={primaryCta.to}
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      px: 3.5, py: 1.6, fontWeight: 700,
                      fontSize: '0.95rem',
                      background: 'linear-gradient(135deg, #B87333 0%, #D4924D 100%)',
                      color: '#ffffff',
                      boxShadow: '0 10px 28px rgba(184,115,51,0.45)',
                      transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #9A5F22 0%, #B87333 100%)',
                        boxShadow: '0 14px 36px rgba(184,115,51,0.55)',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    {primaryCta.label}
                  </MuiButton>
                )}
                {secondaryCta && (
                  <MuiButton
                    component={RouterLink}
                    to={secondaryCta.to}
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{
                      px: 3.2, py: 1.5,
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      boxShadow: '0 10px 28px rgba(138,154,91,0.45)',
                      transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                      '&:hover': {
                        boxShadow: '0 14px 36px rgba(138,154,91,0.55)',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    {secondaryCta.label}
                  </MuiButton>
                )}
              </Stack>
            )}
          </Box>

          {/* Mobile image */}
          <Box
            component="img"
            src={image.src}
            alt={image.alt}
            loading="eager"
            sx={{
              display: { xs: 'block', md: 'none' },
              width: 'calc(100% + 48px)',
              ml: -3,
              height: 280,
              objectFit: 'cover',
              objectPosition: 'center 20%',
              mt: 5,
            }}
          />
        </Container>

        {/* Desktop image */}
        <Box
          component="img"
          src={image.src}
          alt={image.alt}
          loading="eager"
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            top: 0,
            right: { sm: '2%', md: '4%', lg: '6%' },
            height: '100%',
            width: 'auto',
            maxWidth: '44%',
            objectFit: 'cover',
          }}
        />
      </Box>
    )
  }

  // Default layout (dark bg, circular image)
  const textColor = '#fff'
  return (
    <Box
      sx={{
        background: 'radial-gradient(circle at top, rgba(184,115,51,0.28), transparent 40%), linear-gradient(135deg, #06141A 0%, #0B2027 55%, #112836 100%)',
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -120,
          right: -120,
          width: 280,
          height: 280,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
          filter: 'blur(2px)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 5,
          background: 'linear-gradient(90deg, #B87333 0%, #D4924D 50%, #B87333 100%)',
        },
      }}
    >
      <Container>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          <Grid size={{ xs: 12, md: image ? 7 : 12 }}>
            <Typography
              variant="h1"
              sx={{
                color: textColor,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                mb: 3,
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
              }}
            >
              {headline}
            </Typography>
            {subtext && (
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: { xs: '1rem', md: '1.15rem' },
                  mb: 4,
                  maxWidth: 620,
                }}
              >
                {subtext}
              </Typography>
            )}
            {(primaryCta || secondaryCta) && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {primaryCta && (
                  <MuiButton
                    component={RouterLink}
                    to={primaryCta.to}
                    variant="contained"
                    color="secondary"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      px: 3.2, py: 1.6, fontWeight: 700,
                      background: 'linear-gradient(135deg, #B87333 0%, #D4924D 100%)',
                      color: '#ffffff',
                      boxShadow: '0 14px 30px rgba(184,115,51,0.45)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #9A5F22 0%, #B87333 100%)',
                        boxShadow: '0 18px 36px rgba(0,0,0,0.28)',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    {primaryCta.label}
                  </MuiButton>
                )}
                {secondaryCta && (
                  <MuiButton
                    component={RouterLink}
                    to={secondaryCta.to}
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: 'rgba(255,255,255,0.6)',
                      color: textColor,
                      px: 3,
                      py: 1.5,
                      '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
                    }}
                  >
                    {secondaryCta.label}
                  </MuiButton>
                )}
              </Stack>
            )}
          </Grid>
          {image && (
            <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                component="img"
                src={image.src}
                alt={image.alt}
                loading="eager"
                sx={{
                  width: { xs: 220, md: 320 },
                  height: { xs: 220, md: 320 },
                  objectFit: 'cover',
                  borderRadius: '50%',
                  boxShadow: '0 20px 48px rgba(11, 32, 39, 0.35)',
                }}
              />
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  )
}
